import { useMemo, useState, useEffect, useCallback } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, ListGroup, Row, Spinner, Tab, Table, Tabs } from "react-bootstrap";
import { useMsal } from "@azure/msal-react";
import { Link } from "react-router-dom";
import { canUseResourceHealthDemo } from "./resourceHealthDemo";
import { useTheme } from "@/hooks/useTheme";
import { useTour } from "@/hooks/useTour";
import { TourOverlay } from "@/components/TourOverlay";
import ResourceComplianceHelpModal from "@/components/ResourceCompliance/ResourceComplianceHelpModal";
import { getComplianceScoreColors } from "@/utils/complianceColorUtils";
import { createResourceComplianceTourSteps } from "./complianceTourSteps";
import styles from "./ResourceCompliancePage.module.scss";
import { getMyRoles } from "@/api/adminApi";
import { searchUsers } from "@/services/resourcesApi";
import {
  getComplianceBusinessUnitSummary,
  getComplianceDirectReports,
  getComplianceOverview,
  getCompliancePersonResources,
  getCompliancePersonSummary,
  getComplianceSnapshots,
} from "@/services/resourceComplianceService";
import {
  ComplianceBusinessUnitSummary,
  ComplianceDirectReport,
  ComplianceOverview,
  CompliancePersonResource,
  CompliancePersonSummary,
  ComplianceSnapshot,
} from "@/types/resourceCompliance";

interface UserSearchResult {
  displayName?: string;
  mail?: string;
  userPrincipalName?: string;
  email?: string;
  upn?: string;
}

function formatNumber(value?: number) {
  if (value === undefined || value === null) return "-";
  return new Intl.NumberFormat().format(value);
}

function formatPercent(value?: number) {
  if (value === undefined || value === null) return "-";
  return `${value.toFixed(2)}%`;
}

function formatGbFromMb(value?: number) {
  if (value === undefined || value === null) return "-";
  const gb = value / 1024;
  return `${new Intl.NumberFormat(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(gb)} GB`;
}

function humanizeFieldName(fieldName: string): string {
  // Strip "Ntid" suffix
  let cleaned = fieldName.replace(/Ntid\s*$/i, "").trim();
  
  // Convert camelCase to Title Case with spaces
  return cleaned
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

const ResourceCompliancePage: React.FC = () => {
  const { accounts } = useMsal();

  const inferredEmail = useMemo(() => {
    return accounts[0]?.username ?? "";
  }, [accounts]);

  const [loading, setLoading] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [snapshots, setSnapshots] = useState<ComplianceSnapshot[]>([]);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null);

  const [overview, setOverview] = useState<ComplianceOverview | null>(null);
  const [buSummary, setBuSummary] = useState<ComplianceBusinessUnitSummary[]>([]);

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [canUseDemo, setCanUseDemo] = useState(false);
  const [isCurrentUserLeader, setIsCurrentUserLeader] = useState(false);
  const [selectedPersonEmail, setSelectedPersonEmail] = useState("");
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [personSearchInput, setPersonSearchInput] = useState("");
  const [showPersonSuggestions, setShowPersonSuggestions] = useState(false);
  const [personSuggestionsLoading, setPersonSuggestionsLoading] = useState(false);
  const [personSuggestions, setPersonSuggestions] = useState<Array<{ displayName: string; email: string }>>([]);

  const [personSummary, setPersonSummary] = useState<CompliancePersonSummary | null>(null);
  const [directReports, setDirectReports] = useState<ComplianceDirectReport[]>([]);
  const [personResources, setPersonResources] = useState<CompliancePersonResource[]>([]);

  // Initialize tour system with tab activation callbacks (memoized to prevent infinite loops)
  const tourSteps = useMemo(
    () => createResourceComplianceTourSteps(setActiveTab),
    []  // setActiveTab is stable, no deps needed
  );
  const tour = useTour({
    steps: tourSteps,
    isSuperAdmin,
  });

  // Log when tour activates/deactivates
  useEffect(() => {
    if (tour.isActive) {
      console.log(`[ResourceCompliancePage] 🎯 Tour started (${tour.totalSteps} steps)`);
    } else {
      console.log(`[ResourceCompliancePage] ❌ Tour ended`);
    }
  }, [tour.isActive]);

  // Log step changes
  useEffect(() => {
    if (tour.isActive && tour.currentStepData) {
      console.log(`[ResourceCompliancePage] Step ${tour.currentStep + 1}/${tour.totalSteps}: ${tour.currentStepData.id}`);
    }
  }, [tour.currentStep, tour.isActive, tour.currentStepData?.id]);

  // Component mount logging
  useEffect(() => {
    console.log('[ResourceCompliancePage] Component mounted - ready for tour');
  }, []);

  // Get current theme for missing fields gradient
  const { accent } = useTheme();

  const getBadgeClassName = (index: number): string => {
    if (index === 0) return styles.badge0;
    if (index === 1) return styles.badge1;
    if (index === 2) return styles.badge2;
    if (index === 3) return styles.badge3;
    if (index === 4) return styles.badge4;
    return styles.badge4; // Use badge4 for 5+ fields
  };

  const renderMissingFields = useCallback((missingFields?: string) => {
    if (!missingFields) return "-";
    const fields = missingFields
      .split(/[,;]/)
      .map((f) => f.trim())
      .filter((f) => f.length > 0);
    if (fields.length === 0) return "-";
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        {fields.map((field, index) => (
          <span
            key={field}
            className={`badge ${getBadgeClassName(index)}`}
            style={{ whiteSpace: "nowrap" }}
          >
            {humanizeFieldName(field)}
          </span>
        ))}
      </div>
    );
  }, []);

  // Initialize current user email
  useEffect(() => {
    setSelectedPersonEmail((prev) => prev || inferredEmail);
  }, [inferredEmail]);

  useEffect(() => {
    if (!isSuperAdmin) {
      setPersonSuggestions([]);
      setShowPersonSuggestions(false);
      setPersonSuggestionsLoading(false);
      return;
    }

    const term = personSearchInput.trim();
    if (!showPersonSuggestions || term.length < 2) {
      setPersonSuggestions([]);
      setPersonSuggestionsLoading(false);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        setPersonSuggestionsLoading(true);
        const found = await searchUsers(term);
        if (cancelled) return;

        const mapped = (found as UserSearchResult[])
          .map((u, idx) => {
            const email = (u.mail || u.userPrincipalName || u.email || u.upn || "").trim();
            const displayName = (u.displayName || email || `User ${idx + 1}`).trim();
            if (!email) return null;
            return { displayName, email };
          })
          .filter((v): v is { displayName: string; email: string } => Boolean(v))
          .slice(0, 8);

        setPersonSuggestions(mapped);
      } catch {
        if (!cancelled) setPersonSuggestions([]);
      } finally {
        if (!cancelled) setPersonSuggestionsLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [isSuperAdmin, personSearchInput, showPersonSuggestions]);

  // Load snapshots and determine admin status
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [snapshotRows, roles] = await Promise.all([
          getComplianceSnapshots(),
          getMyRoles(),
        ]);

        setSnapshots(snapshotRows);
        if (snapshotRows.length > 0) {
          const latestId = snapshotRows[0].snapshotId;
          setSelectedSnapshotId(latestId);
        }

        const superAdmin = roles.some((role) => role.role === "SuperAdmin");
        setIsSuperAdmin(superAdmin);
        setCanUseDemo(canUseResourceHealthDemo(roles));
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || "Failed to load snapshots");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [inferredEmail]);

  const loadPersonData = async (email: string, isCurrentUser: boolean = false) => {
    if (!selectedSnapshotId || !email.trim()) {
      setError("Snapshot and email are required");
      return;
    }

    setOrgLoading(true);
    setError(null);

    try {
      const targetEmail = email.trim();
      const [person, reports, resources] = await Promise.all([
        getCompliancePersonSummary(selectedSnapshotId, targetEmail),
        getComplianceDirectReports(selectedSnapshotId, targetEmail),
        getCompliancePersonResources(selectedSnapshotId, targetEmail),
      ]);

      setPersonSummary(person);
      setDirectReports(reports);
      setPersonResources(resources);

      // If this is the current user, determine if they're a leader
      if (isCurrentUser) {
        const isLeader = (person.levelsBelow > 0) || (person.resourcesAccountableFor > 0) || (person.storageAccountableFor > 0);
        setIsCurrentUserLeader(isLeader);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to load person health data");
      setPersonSummary(null);
      setDirectReports([]);
      setPersonResources([]);
    } finally {
      setOrgLoading(false);
    }
  };

  useEffect(() => {
    const loadSummary = async () => {
      if (!selectedSnapshotId) return;
      setLoading(true);
      setError(null);
      try {
        const [overviewData, buData] = await Promise.all([
          getComplianceOverview(selectedSnapshotId),
          getComplianceBusinessUnitSummary(selectedSnapshotId),
        ]);

        setOverview(overviewData);
        setBuSummary([...buData].sort((a, b) => a.averageComplianceScore - b.averageComplianceScore));
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || "Failed to load health summary");
      } finally {
        setLoading(false);
      }
    };

    loadSummary();
  }, [selectedSnapshotId]);

  useEffect(() => {
    if (!selectedSnapshotId || !inferredEmail) return;
    void loadPersonData(inferredEmail, true);
  }, [selectedSnapshotId, inferredEmail]);

  // Auto-load data when selectedPersonEmail changes
  useEffect(() => {
    if (!selectedSnapshotId || !selectedPersonEmail) return;
    void loadPersonData(selectedPersonEmail);
  }, [selectedSnapshotId, selectedPersonEmail]);

  const loadSelectedPerson = async () => {
    await loadPersonData(selectedPersonEmail);
  };

  return (
    <Container fluid className="py-4" data-tour-id="page-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0">Microsoft 365 Resource Health</h4>
          <div className="text-muted">Snapshot-backed resource health dashboard</div>
        </div>
        <div className="d-flex gap-2 align-items-center">
          {canUseDemo && <Link className="btn btn-outline-primary" to="/resource-compliance/demo">Walk-up demo</Link>}
          <Button
            variant="outline-primary"
            className="fw-semibold px-3"
            onClick={tour.startTour}
            aria-label="Start Resource Health Tour"
          >
            Take Tour
          </Button>
          <Button
            variant="warning"
            data-tour-id="btn-need-help"
            className="fw-semibold px-3"
            onClick={() => setShowHelpModal(true)}
            aria-label="Open Resource Health Help"
          >
            Need Help?
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-3" data-tour-id="snapshots-section">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={12} lg={8}>
              <Form.Group>
                <Form.Label>Snapshot</Form.Label>
                <Form.Select
                  value={selectedSnapshotId ?? ""}
                  onChange={(e) => setSelectedSnapshotId(e.target.value || null)}
                  disabled={loading || snapshots.length === 0}
                  data-tour-id="snapshots-list"
                >
                  {snapshots.map((s) => (
                    <option key={s.snapshotId} value={s.snapshotId}>
                      {s.snapshotName} ({new Date(s.snapshotDt).toLocaleDateString()})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {(loading || orgLoading) && (
        <div className="mb-3">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading...
        </div>
      )}

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'overview')} className="mb-3" data-tour-id="overview-tabs">
        <Tab eventKey="overview" title="Overview" data-tour-id="overview-section">
          <Card className="mb-3 border-0 shadow-sm">
            <Card.Body>
              <Row className="g-3 align-items-stretch text-center">
                <Col md={4}>
                  <div className="h-100 d-flex flex-column justify-content-between border-end pe-md-3">
                    <div>
                      <span className="badge fs-2 px-3 py-2" style={getComplianceScoreColors(overview?.averageComplianceScore)}>
                        {formatPercent(overview?.averageComplianceScore)}
                      </span>
                    </div>
                    <div className="small text-muted text-uppercase">Resource Health</div>
                    <div className="small text-muted mt-3">
                      Healthy {formatNumber(overview?.compliantResources)} | Unhealthy {formatNumber(overview?.nonCompliantResources)}
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="h-100 d-flex flex-column justify-content-start border-end px-md-3">
                    <div className="fs-1 fw-bold">{formatNumber(overview?.totalResources)}</div>
                    <div className="small text-muted text-uppercase">Total Resources</div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="h-100 d-flex flex-column justify-content-start ps-md-3">
                    <div className="fs-1 fw-bold">{formatGbFromMb(overview?.totalStorageMb)}</div>
                    <div className="small text-muted text-uppercase">Total Storage</div>
                  </div>
                </Col>
              </Row>

              <div className="border-top mt-4 pt-4 text-center" style={{ fontSize: "1.1rem" }}>
                <strong className="me-3">Resource Reviews</strong>
                <span className="me-3">Due now <span className="fw-bold">{formatNumber(overview?.dueNow)}</span></span>
                <span className="mx-3">|</span>
                <span className="mx-3">This quarter <span className="fw-bold">{formatNumber(overview?.dueThisQuarter)}</span></span>
                <span className="mx-3">|</span>
                <span className="mx-3">Next quarter <span className="fw-bold">{formatNumber(overview?.dueNextQuarter)}</span></span>
                <span className="mx-3">|</span>
                <span className="ms-3">Next 12 months <span className="fw-bold">{formatNumber(overview?.dueNext12Months)}</span></span>
              </div>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="bu" title="BU Summary" data-tour-id="business-unit-summary">
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Business Unit</th>
                <th>Resource Count</th>
                <th>Storage GB</th>
                <th>Avg Health</th>
                <th>Healthy</th>
                <th>Unhealthy</th>
                <th>Due Now</th>
                <th>Due This Quarter</th>
                <th>Due Next Quarter</th>
                <th>Due Next 12 Months</th>
              </tr>
            </thead>
            <tbody>
              {buSummary.map((row) => (
                <tr key={row.businessUnit}>
                  <td>{row.businessUnit === "OneDrive" ? <Badge bg="secondary">OneDrive</Badge> : row.businessUnit}</td>
                  <td className="text-end">{formatNumber(row.resourceCount)}</td>
                  <td className="text-end">{formatGbFromMb(row.storageMb)}</td>
                  <td className="text-end">
                    {row.businessUnit === "OneDrive" ? (
                      <span className="text-muted">N/A</span>
                    ) : (
                      <span className="badge" style={getComplianceScoreColors(row.averageComplianceScore)}>
                        {formatPercent(row.averageComplianceScore)}
                      </span>
                    )}
                  </td>
                  <td className="text-end">{row.businessUnit === "OneDrive" ? <span className="text-muted">N/A</span> : formatNumber(row.compliantResources)}</td>
                  <td className="text-end">{row.businessUnit === "OneDrive" ? <span className="text-muted">N/A</span> : formatNumber(row.nonCompliantResources)}</td>
                  <td className="text-end">{row.businessUnit === "OneDrive" ? <span className="text-muted">N/A</span> : formatNumber(row.dueNow)}</td>
                  <td className="text-end">{row.businessUnit === "OneDrive" ? <span className="text-muted">N/A</span> : formatNumber(row.dueThisQuarter)}</td>
                  <td className="text-end">{row.businessUnit === "OneDrive" ? <span className="text-muted">N/A</span> : formatNumber(row.dueNextQuarter)}</td>
                  <td className="text-end">{row.businessUnit === "OneDrive" ? <span className="text-muted">N/A</span> : formatNumber(row.dueNext12Months)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Tab>

        <Tab eventKey="org" title="My Organization">
          {isSuperAdmin && (
            <Card className="mb-3">
              <Card.Body>
                <Row className="g-3 align-items-end">
                  <Col lg={8}>
                    <Form.Group className="position-relative" data-tour-id="person-search">
                      <Form.Label>Person (search any user)</Form.Label>
                      <Form.Control
                        value={personSearchInput}
                        onChange={(e) => {
                          const value = e.target.value;
                          setPersonSearchInput(value);
                          setSelectedPersonEmail(value);
                          setShowPersonSuggestions(true);
                        }}
                        onFocus={() => setShowPersonSuggestions(true)}
                        onBlur={() => window.setTimeout(() => setShowPersonSuggestions(false), 150)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            void loadSelectedPerson();
                          }
                        }}
                        autoComplete="off"
                        placeholder="Search by name or email"
                        disabled={orgLoading}
                      />

                      {showPersonSuggestions && (
                        <div className="position-absolute bg-white border rounded shadow-sm w-100 mt-1" style={{ zIndex: 1000, maxHeight: 260, overflowY: "auto" }}>
                          {personSuggestionsLoading && (
                            <div className="p-2 small text-muted">
                              <Spinner animation="border" size="sm" className="me-2" />
                              Searching...
                            </div>
                          )}

                          {!personSuggestionsLoading && personSuggestions.length > 0 && (
                            <ListGroup variant="flush">
                              {personSuggestions.map((person, idx) => (
                                <ListGroup.Item
                                  key={`${person.email}-${idx}`}
                                  action
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setSelectedPersonEmail(person.email);
                                    setPersonSearchInput(person.email);
                                    setShowPersonSuggestions(false);
                                  }}
                                >
                                  <div className="fw-semibold">{person.displayName}</div>
                                  <div className="small text-muted">{person.email}</div>
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                          )}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={4}>
                    <Button className="w-100" onClick={loadSelectedPerson} disabled={orgLoading || !selectedSnapshotId || !selectedPersonEmail} data-tour-id="update-metadata-cta">
                      {orgLoading ? "Loading..." : "Load"}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {!isSuperAdmin && isCurrentUserLeader && (
            <Card className="mb-3">
              <Card.Body>
                <Row className="g-3 align-items-end">
                  <Col lg={8}>
                    <Form.Group data-tour-id="person-search">
                      <Form.Label>Direct Reports</Form.Label>
                      <Form.Control
                        as="select"
                        value={selectedPersonEmail}
                        onChange={(e) => setSelectedPersonEmail(e.target.value)}
                        disabled={orgLoading}
                      >
                        <option value={inferredEmail}>{personSummary?.employeeDisplayName || inferredEmail} (You)</option>
                        {directReports.map((report) => (
                          <option key={report.directReportEmail} value={report.directReportEmail}>
                            {report.directReportDisplayName || report.directReportEmail}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col lg={4}>
                    <Button
                      className="w-100"
                      onClick={() => loadPersonData(selectedPersonEmail)}
                      disabled={orgLoading || !selectedSnapshotId || !selectedPersonEmail}
                      data-tour-id="update-metadata-cta"
                    >
                      {orgLoading ? "Loading..." : "Load"}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {personSummary ? (
            <>
              <Card className="mb-3 shadow-sm border-0" data-tour-id="org-structure-summary">
                <Card.Body>
                  <Row className="g-4">
                    <Col lg={4}>
                      <h5 className="mb-1">{personSummary.employeeDisplayName || personSummary.employeeEmail}</h5>
                      <div className="text-muted mb-3">{personSummary.employeeEmail}</div>
                      <div><strong>Business unit:</strong> {personSummary.businessUnit || "Unknown"}</div>
                      <div><strong>Manager:</strong> {personSummary.managerEmail || "-"}</div>
                      {personSummary.levelsBelow > 0 && <div><strong>Levels below:</strong> {formatNumber(personSummary.levelsBelow)}</div>}
                    </Col>
                    <Col md={6} lg={4}>
                      <div className="text-muted small text-uppercase mb-2">Ownership</div>
                      <div className="mb-2"><strong>Resources owned:</strong> {formatNumber(personSummary.resourcesOwned)}</div>
                      <div className="mb-2"><strong>Storage owned:</strong> {formatGbFromMb(personSummary.storageOwned)}</div>
                      {personSummary.levelsBelow > 0 && (
                        <>
                          <div className="mb-2"><strong>Resources accountable:</strong> {formatNumber(personSummary.resourcesAccountableFor)}</div>
                          <div><strong>Storage accountable:</strong> {formatGbFromMb(personSummary.storageAccountableFor)}</div>
                        </>
                      )}
                    </Col>
                    <Col md={6} lg={4} data-tour-id="compliance-score">
                      <div className="text-muted small text-uppercase mb-2">Health and Reviews</div>
                      <div className="mb-2">
                        <strong>Direct health:</strong>{" "}
                        <span className="badge" style={getComplianceScoreColors(personSummary.directComplianceScore)}>
                          {formatPercent(personSummary.directComplianceScore)}
                        </span>
                      </div>
                      {personSummary.levelsBelow > 0 && (
                        <div className="mb-2" data-tour-id="org-recursive-score">
                          <strong>Recursive health:</strong>{" "}
                          <span className="badge" style={getComplianceScoreColors(personSummary.recursiveComplianceScore)}>
                            {formatPercent(personSummary.recursiveComplianceScore)}
                          </span>
                        </div>
                      )}
                      <div className="mb-2"><strong>Due now:</strong> {formatNumber(personSummary.reviewsDueNow)}</div>
                      <div className="mb-2"><strong>Due this quarter:</strong> {formatNumber(personSummary.reviewsDueThisQuarter)}</div>
                      <div className="mb-2"><strong>Due next quarter:</strong> {formatNumber(personSummary.reviewsDueNextQuarter)}</div>
                      <div><strong>Due next 12 months:</strong> {formatNumber(personSummary.reviewsDueNext12Months)}</div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {directReports.length > 0 && (
                <>
                  <h6 data-tour-id="direct-reports-list">Direct Reports</h6>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Name</th>
                        <th>Resources Accountable</th>
                        <th>Storage (GB)</th>
                        <th>Health</th>
                        <th>Due Now</th>
                        <th>Due Next Quarter</th>
                      </tr>
                    </thead>
                    <tbody>
                      {directReports.map((r) => (
                        <tr key={`${r.leaderEmail}-${r.directReportEmail}`}>
                          <td>{r.directReportEmail}</td>
                          <td>{r.directReportDisplayName || "-"}</td>
                          <td className="text-end">{formatNumber(r.resourcesAccountableFor)}</td>
                          <td className="text-end">{formatGbFromMb(r.storageAccountableFor)}</td>
                          <td className="text-end">
                            {r.complianceScore !== undefined && r.complianceScore !== null ? (
                              <span className="badge" style={getComplianceScoreColors(r.complianceScore)}>
                                {formatPercent(r.complianceScore)}
                              </span>
                            ) : (
                              "N/A"
                            )}
                          </td>
                          <td className="text-end">{formatNumber(r.reviewsDueNow)}</td>
                          <td className="text-end">{formatNumber(r.reviewsDueNextQuarter)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          ) : (
            <Alert variant="info">Click "Load Data" to view health details.</Alert>
          )}
        </Tab>

        <Tab eventKey="myresources" title="My Resources">
            {personSummary ? (
              <>
                <Card className="mb-3 shadow-sm border-0">
                  <Card.Body>
                    <Row className="g-4 align-items-start">
                      <Col lg={4}>
                        <div className="d-flex gap-3">
                          {/* Profile Image Circle */}
                          <div
                            style={{
                              width: 80,
                              height: 80,
                              borderRadius: "50%",
                              backgroundColor: "#e9ecef",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              overflow: "hidden",
                              border: "2px solid #dee2e6",
                            }}
                          >
                            {personSummary.photoUrl ? (
                              <img
                                src={personSummary.photoUrl}
                                alt={personSummary.employeeDisplayName || personSummary.employeeEmail}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            ) : (
                              <span style={{ fontSize: "1.8rem", color: "#6c757d", fontWeight: "bold" }}>
                                {(personSummary.employeeDisplayName || personSummary.employeeEmail)
                                  .charAt(0)
                                  .toUpperCase()}
                              </span>
                            )}
                          </div>
                          {/* Name, Email, Resource Count */}
                          <div className="d-flex flex-column justify-content-center">
                            <h5 className="mb-1">{personSummary.employeeDisplayName || personSummary.employeeEmail}</h5>
                            <div className="text-muted small mb-2">{personSummary.employeeEmail}</div>
                            <div className="text-muted small">{personResources.length} resource{personResources.length === 1 ? "" : "s"} in current snapshot</div>
                          </div>
                        </div>
                      </Col>
                      <Col md={6} lg={4}>
                        <div className="text-muted small text-uppercase mb-2">Ownership</div>
                        <div className="mb-2"><strong>Healthy owned:</strong> {formatNumber(personSummary.compliantResourcesOwned)}</div>
                        <div className="mb-2"><strong>Unhealthy owned:</strong> {formatNumber(personSummary.nonCompliantResourcesOwned)}</div>
                        <div><strong>Storage owned:</strong> {formatGbFromMb(personSummary.storageOwned)}</div>
                      </Col>
                      {personSummary.levelsBelow > 0 && (
                        <Col md={6} lg={4}>
                          <div className="text-muted small text-uppercase mb-2">Accountable Portfolio</div>
                          <div className="mb-2"><strong>Healthy accountable:</strong> {formatNumber(personSummary.compliantResourcesAccountableFor)}</div>
                          <div className="mb-2"><strong>Unhealthy accountable:</strong> {formatNumber(personSummary.nonCompliantResourcesAccountableFor)}</div>
                          <div><strong>Storage accountable:</strong> {formatGbFromMb(personSummary.storageAccountableFor)}</div>
                        </Col>
                      )}
                    </Row>
                  </Card.Body>
                </Card>

                <Table striped bordered hover responsive key={`myresources-${accent}`} data-tour-id="org-all-resources">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>URL</th>
                      <th>Resource Type</th>
                      <th>Health</th>
                      <th>Missing Fields</th>
                      <th>Review Status</th>
                      <th>Storage (GB)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {personResources.map((r, idx) => (
                      <tr key={`${r.title || "resource"}-${idx}-${accent}`}>
                        <td>{r.title || "-"}</td>
                        <td>
                          {r.url ? (
                            <a href={r.url} target="_blank" rel="noreferrer">
                              {r.url}
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>{r.resourceType || "-"}</td>
                        <td className="text-end">
                          {r.complianceScore !== undefined && r.complianceScore !== null ? (
                            <span className="badge" style={getComplianceScoreColors(r.complianceScore)}>
                              {formatPercent(r.complianceScore)}
                            </span>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>{renderMissingFields(r.missingFields)}</td>
                        <td>{r.reviewStatus || "-"}</td>
                        <td className="text-end">{formatGbFromMb(r.storageMb)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            ) : (
              <Alert variant="info">Resource snapshot will appear after a person is loaded.</Alert>
            )}
          </Tab>
        </Tabs>

        {tour.isActive && !loading && !orgLoading && (
          <TourOverlay
            isActive={tour.isActive}
            step={tour.currentStepData}
            stepNumber={tour.currentStep}
            totalSteps={tour.totalSteps}
            onNext={tour.goToNextStep}
            onPrevious={tour.goToPreviousStep}
            onComplete={tour.completeTour}
          />
        )}

        <ResourceComplianceHelpModal
          show={showHelpModal}
          onHide={() => setShowHelpModal(false)}
        />
      </Container>
    );
  };

  export default ResourceCompliancePage;
